import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Button, Flex, Grid, Spinner, Box, Center,
  useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, ModalFooter, FormControl,
  FormLabel, Alert, AlertIcon, Text, UnorderedList, ListItem,
  HStack, Input, Tooltip, Select, FormErrorMessage, Checkbox, VStack,
  IconButton, Fade, Skeleton, SkeletonText, useColorModeValue,
  SimpleGrid, Icon
} from "@chakra-ui/react";
import { AddIcon, RepeatIcon } from "@chakra-ui/icons";
import { FiUsers, FiUserPlus, FiAlertCircle, FiBookOpen } from 'react-icons/fi';
import { useForm } from "react-hook-form";
import { format } from 'rut.js';

import * as XLSX from 'xlsx';
import { getCustomers, createCustomer, getProductsByCategories, updateCustomer, getProducts } from "services/woo";
import { searchCustomers } from 'services/unifiedCustomersSearch';
import { signUpApoderado } from "services/userService";
import { getSiteConfig } from 'utils/site';
import { buildNormalizedGroupOptions, getUserGroups } from 'utils/courseUtils';
import Customers from "./components/Customers";
import DynamicPaginator from "components/Tables/DynamicPaginator";
import { withModuleCheck, useModuleCheck, MODULES, useUserAuth } from "utils/site";
import { getSurveys } from "services/surveysService"; 
import SurveyGroupSelector from "./SurveyGroupSelector";
import { useDebouncedCallback } from 'use-debounce';

// Opciones del filtro de estado:
// - Admin: ve los roles tal cual (customer, becado, cobranza, subscriber) + "Activo (todos)"
// - Resto: "Activo" agrupa customer + becado + cobranza; "Archivado" = subscriber
const buildRoleOptions = (isAdmin) => (isAdmin ? [
  { value: '', label: 'Todos los estados' },
  { value: 'activo', label: 'Activo (todos)' },
  { value: 'customer', label: 'Customer' },
  { value: 'becado', label: 'Becado' },
  { value: 'cobranza', label: 'Cobranza' },
  { value: 'subscriber', label: 'Subscriber (Archivado)' }
] : [
  { value: '', label: 'Todos los estados' },
  { value: 'activo', label: 'Activo' },
  { value: 'subscriber', label: 'Archivado' }
]);

function Suscriptions({ 
  defaultGroup = '', 
  showFilters = {
    surveys: true,      // Si se muestra el filtro de encuestas
  },
  // Nuevas props para controlar el comportamiento de Customers
  customersConfig = {
    showActionsMenu: true,        // Si se muestra el menú de acciones
    defaultAction: null,          // Acción por defecto ('tickets', 'group', null)
    title: "Usuarios"             // Título personalizable
  }
}) {
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();
  const { user: currentUser } = useUserAuth();
  const isAdmin = currentUser?.role === 'administrador';

  const isSearchingRef = useRef(false);

  const [selectedSurveys, setSelectedSurveys] = useState('');
  const [surveysList, setSurveysList] = useState([]);
  const [includeUserSurveys, setIncludeUserSurveys] = useState(false);


  const debouncedFetchCustomers = useDebouncedCallback(
    (options) => {
      fetchCustomers(options);
    },
    300 // 300ms de espera entre llamadas (más responsivo)
  );

  // Estado de módulos - memoizado
  const moduleStatus = useMemo(() => ({
    [MODULES.PAYMENTS]: useModuleCheck(MODULES.PAYMENTS),
    [MODULES.TICKETS]: useModuleCheck(MODULES.TICKETS),
    [MODULES.SURVEYS]: useModuleCheck(MODULES.SURVEYS),
    [MODULES.FILES]: useModuleCheck(MODULES.FILES),
    [MODULES.SUBSCRIPTIONS]: useModuleCheck(MODULES.SUBSCRIPTIONS)
  }), []);

  // Funciones para manejar localStorage
  const saveFiltersToLocalStorage = useCallback((filtersData, currentPageToSave = null) => {
    try {
      const filtersToSave = {
        filters: filtersData,
        itemsPerPage,
        currentPage: currentPageToSave !== null ? currentPageToSave : currentPage,
        timestamp: Date.now()
      };
      localStorage.setItem('suscriptions-filters', JSON.stringify(filtersToSave));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [itemsPerPage, currentPage]);

  const loadFiltersFromLocalStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem('suscriptions-filters');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Verificar que no sea muy antiguo (24 horas)
        if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          // Excluir filtros ocultos que pudieron haberse guardado antes
          const HIDDEN_FILTERS = ['suscription', 'surveys_responded'];
          if (parsed.filters) {
            parsed.filters = parsed.filters
              .filter(f => !HIDDEN_FILTERS.includes(f.name))
              .map(f => f.name === 'group' ? { ...f, placeholder: 'CURSOS' } : f);
          }
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }
    return null;
  }, []);

  // Initial filters generator
  const getInitialFilters = useCallback(() => {
    const baseFilters = [];

    if (showFilters.searchRaw !== false) {
      baseFilters.push({
        name: 'searchRaw',
        type: 'searchRaw',
        value: '',
        placeholder: 'Buscar usuario por nombre, email o RUT...'
      });
    }

    if (showFilters.group !== false) {
      baseFilters.push({
        name: 'group',
        type: 'select',
        value: defaultGroup,
        placeholder: 'CURSOS',
        options: [
          { value: '', label: 'TODOS LOS CURSOS' },
          { value: 'empty', label: 'SIN CURSO ASIGNADO' }
        ]
      });
    }

    if (showFilters.role !== false) {
      const roleOptions = buildRoleOptions(isAdmin);
      baseFilters.push({
        name: 'role',
        type: 'select',
        value: '',
        placeholder: 'ESTADO',
        options: roleOptions
      });
    }

    return baseFilters;
  }, [moduleStatus, showFilters, defaultGroup, isAdmin]);
  // Cargar filtros desde localStorage al inicializar
  const initializeFilters = useCallback(() => {
    const savedData = loadFiltersFromLocalStorage();
    if (savedData) {
      return savedData.filters;
    }
    return getInitialFilters();
  }, [loadFiltersFromLocalStorage, getInitialFilters]);

  // Inicializar estados desde localStorage con valores por defecto seguros
  const getInitialStates = useCallback(() => {
    try {
      const savedData = loadFiltersFromLocalStorage();
      if (savedData?.filters) {
        const filters = savedData.filters;
        return {
          valueRUT: filters.find(f => f.name === 'searchRaw')?.value || '',
          selectedRole: filters.find(f => f.name === 'role')?.value || '',
          selectedGroup: filters.find(f => f.name === 'group')?.value || '',
          selectedSuscription: filters.find(f => f.name === 'suscription')?.value || '',
          selectedSurveys: filters.find(f => f.name === 'surveys_responded')?.value?.join(',') || '',
          includeUserSurveys: !!(filters.find(f => f.name === 'surveys_responded')?.value?.length),
          currentPage: savedData.currentPage || 1
        };
      }
    } catch (error) {
      console.error('Error loading initial states:', error);
    }
    
    // Valores por defecto seguros
    return {
      valueRUT: '',
      selectedRole: '',
      selectedGroup: '',
      selectedSuscription: '',
      selectedSurveys: '',
      includeUserSurveys: false,
      currentPage: 1
    };
  }, [loadFiltersFromLocalStorage]);

  const [loading, setLoading] = useState(true); // Un solo estado de loading
  const [isExporting, setIsExporting] = useState(false);
  const [groups, setGroups] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [valueRUT, setValueRUT] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedSuscription, setSelectedSuscription] = useState('');
  const [suscriptions, setSuscriptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const savedData = loadFiltersFromLocalStorage();
      return savedData?.currentPage || 1;
    } catch (error) {
      console.error('Error loading currentPage:', error);
      return 1;
    }
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [loadingNewCustomer, setLoadingNewCustomer] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const savedData = loadFiltersFromLocalStorage();
      return savedData?.itemsPerPage || 20;
    } catch (error) {
      console.error('Error loading itemsPerPage:', error);
      return 20;
    }
  });
  const [filters, setFilters] = useState(initializeFilters);

  // Actualizar opciones del filtro de rol cuando isAdmin carga (useUserAuth es async)
  useEffect(() => {
    setFilters(prev => prev.map(f => {
      if (f.name !== 'role') return f;
      return {
        ...f,
        options: buildRoleOptions(isAdmin)
      };
    }));
  }, [isAdmin]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialRef = useRef(null);
  const finalRef = useRef(null);

  // Referencia para rastrear la última búsqueda realizada
  const lastSearchRef = useRef({
    timestamp: 0,
    params: {}
  });

  // Efecto para cargar estados iniciales desde localStorage
  useEffect(() => {
    const initialStates = getInitialStates();
    
    // Aplicar estados iniciales
    setValueRUT(initialStates.valueRUT);
    setSelectedRole(initialStates.selectedRole);
    setSelectedGroup(initialStates.selectedGroup);
    setSelectedSuscription(initialStates.selectedSuscription);
    setSelectedSurveys(initialStates.selectedSurveys);
    setIncludeUserSurveys(initialStates.includeUserSurveys);
    setCurrentPage(initialStates.currentPage);
  }, [getInitialStates]);


  // Handle filter changes
  const handleFilterChange = useCallback((name, value) => {
    // Actualizar estado individual del filtro cambiado
    if (name === 'group') setSelectedGroup(value);
    if (name === 'role') setSelectedRole(value);
    if (name === 'suscription') setSelectedSuscription(value);
    if (name === 'searchRaw') setValueRUT(value);
    if (name === 'surveys_responded') {
      const str = Array.isArray(value) ? value.join(',') : value;
      setSelectedSurveys(str);
      setIncludeUserSurveys(!!str);
    }

    setFilters(prev => {
      const newFilters = prev.map(f => f.name === name ? {...f, value} : f);
      try {
        localStorage.setItem('suscriptions-filters', JSON.stringify({
          filters: newFilters,
          itemsPerPage,
          currentPage: 1,
          timestamp: Date.now()
        }));
      } catch (error) {
        console.error('Error saving filters to localStorage:', error);
      }
      return newFilters;
    });

    setCurrentPage(1);

    // Construir params con TODOS los filtros actuales, sobreescribiendo el que cambió.
    // Esto evita el stale closure dentro de fetchCustomers (que tiene deps=[]).
    const surveysVal = name === 'surveys_responded'
      ? (Array.isArray(value) ? value.join(',') : value)
      : selectedSurveys;

    debouncedFetchCustomers({
      page: 1,
      suscription: name === 'suscription' ? value : selectedSuscription,
      group:       name === 'group'       ? value : selectedGroup,
      role:        name === 'role'        ? value : selectedRole,
      search:      name === 'searchRaw'   ? value : valueRUT,
      surveys_responded: surveysVal
    });
  }, [debouncedFetchCustomers, itemsPerPage, selectedSuscription, selectedGroup, selectedRole, valueRUT, selectedSurveys]);

  // Si el rol guardado en localStorage no existe entre las opciones del usuario
  // (ej. no-admin con 'customer'/'cobranza' de antes), pasarlo a 'activo' o limpiarlo
  const roleSanitizedRef = useRef(false);
  useEffect(() => {
    if (!currentUser || roleSanitizedRef.current) return;
    roleSanitizedRef.current = true;
    if (!selectedRole) return;
    const valid = buildRoleOptions(isAdmin).some(o => o.value === selectedRole);
    if (valid) return;
    const legacyActive = ['customer', 'becado', 'cobranza'].includes(selectedRole);
    handleFilterChange('role', legacyActive ? 'activo' : '');
  }, [currentUser, isAdmin, selectedRole, handleFilterChange]);

  // Modificar el manejador de cancelación para incluir la limpieza del grupo
  const cancelNewCustomer = () => {
    setValueRUT('');
    reset({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      group: '',
      suscription: ''
    });
    setLoadingNewCustomer(false);
    setFormMessage(null);
    onClose();
  };

  // Versión mejorada de fetchCustomers con prevención de duplicados
  const fetchCustomers = useCallback(async (options = {}) => {
    // Evitar búsquedas duplicadas si ya hay una en curso
    if (isSearchingRef.current) {
      console.log('Búsqueda en curso, ignorando petición:', options);
      return;
    }
    
    // Marcar como búsqueda en curso
    isSearchingRef.current = true;
    
    // Tu código existente de prevención de duplicados puede permanecer
    const paramsHash = JSON.stringify(options);
    const now = Date.now();
    
    // Verificar si esta es una búsqueda duplicada (mismos parámetros en menos de 1 segundo)
    if (
      now - lastSearchRef.current.timestamp < 1000 && 
      JSON.stringify(lastSearchRef.current.params) === paramsHash
    ) {
      console.log('Evitando búsqueda duplicada', options);
      isSearchingRef.current = false; // No olvidar liberar el bloqueo
      return;
    }
    
    lastSearchRef.current = {
      timestamp: now,
      params: {...options}
    };
    
    setLoading(true);
    try {
      const searchParams = {
        page: options.page !== undefined ? options.page : currentPage,
        perPage: options.perPage !== undefined ? options.perPage : itemsPerPage,
        suscriptionName: options.suscription !== undefined ? options.suscription : (options.suscriptionName !== undefined ? options.suscriptionName : selectedSuscription),
        groupName: options.group !== undefined ? options.group : (options.groupName !== undefined ? options.groupName : selectedGroup),
        search: options.search !== undefined ? options.search : valueRUT,
        role: options.role !== undefined ? options.role : selectedRole,
        surveysResponded: options.surveys_responded !== undefined ? options.surveys_responded : selectedSurveys,
        includeSurveys: (options.surveys_responded !== undefined ? options.surveys_responded : selectedSurveys) ? 'false' : undefined
      };

      console.log('🔧 OPTIONS RECIBIDAS:', options);
      console.log('🔧 SEARCH PARAMS CONSTRUIDOS:', searchParams);

      console.log('Ejecutando búsqueda con parámetros:', searchParams);

      // Verificar si solo hay búsqueda de texto sin otros filtros
      const hasOtherFilters = searchParams.suscriptionName || 
                             searchParams.groupName || 
                             searchParams.role || 
                             searchParams.surveysResponded;
      
      const hasSearchTerm = searchParams.search && searchParams.search.trim();
      
      // Usar servicio unificado para búsquedas de texto puras (sin otros filtros)
      if (hasSearchTerm && !hasOtherFilters) {
        console.log('✅ FETCHCUSTOMERS: Usando servicio UNIFICADO para búsqueda mejorada:', searchParams.search);
        
        try {
          const unifiedResults = await searchCustomers(searchParams.search, searchParams.perPage);
          
          if (unifiedResults && Array.isArray(unifiedResults)) {
            setCustomers(unifiedResults);
            setTotalCustomers(unifiedResults.length);
            // Para búsquedas unificadas, asumimos que no hay paginación tradicional
            setTotalPages(1);
            console.log('✅ FETCHCUSTOMERS: Servicio unificado - ', unifiedResults.length, 'resultados encontrados');
          } else {
            setCustomers([]);
            setTotalCustomers(0);
            setTotalPages(1);
          }
        } catch (unifiedError) {
          console.error('Error en servicio unificado, fallback a servicio tradicional:', unifiedError);
          // Fallback al servicio tradicional en caso de error
          const response = await getCustomers(
            searchParams.suscriptionName,
            searchParams.groupName,
            searchParams.search,
            null,
            null,
            searchParams.role,
            searchParams.page,
            searchParams.perPage,
            searchParams.surveysResponded,
            searchParams.includeSurveys
          );

          if (response?.data && Array.isArray(response.data)) {
            setCustomers(response.data);
          } else {
            setCustomers([]);
          }

          if (response?.headers) {
            setTotalCustomers(parseInt(response.headers['x-wp-total']) || 0);
            setTotalPages(parseInt(response.headers['x-wp-totalpages']) || 1);
          }
        }
      } else {
        // Con filtros complejos o sin búsqueda, usar servicio con objeto
          console.log('🔄 FETCHCUSTOMERS: Usando servicio con objeto');

        // group_name puede ser JSON de array de rawValues (dropdown normalizado)
        let resolvedGroupName = searchParams.groupName;
        try {
          const parsed = JSON.parse(searchParams.groupName);
          if (Array.isArray(parsed)) resolvedGroupName = parsed;
        } catch (_) {}

        const objectToSend = {
          suscription_name: searchParams.suscriptionName,
          group_name: resolvedGroupName,
          search: searchParams.search,
          role: searchParams.role,
          page: searchParams.page,
          per_page: searchParams.perPage,
          surveys_responded: searchParams.surveysResponded,
          include_surveys: searchParams.includeSurveys
        };

        const response = await getCustomers(objectToSend);

        if (response?.data && Array.isArray(response.data)) {
          setCustomers(response.data);
        } else {
          setCustomers([]);
        }

        if (response?.headers) {
          setTotalCustomers(parseInt(response.headers['x-wp-total']) || 0);
          setTotalPages(parseInt(response.headers['x-wp-totalpages']) || 1);
        }
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
      setTotalPages(1);
      setTotalCustomers(0);
    } finally {
      setLoading(false);
      // Liberar el bloqueo con un pequeño retraso para evitar llamadas rápidas consecutivas
      setTimeout(() => {
        isSearchingRef.current = false;
      }, 300);
    }
  }, []);  // Sin dependencias - solo será llamado manualmente


  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    saveFiltersToLocalStorage(filters, newPage);
    debouncedFetchCustomers({
      page: newPage,
      suscription: selectedSuscription,
      group: selectedGroup || defaultGroup,
      role: selectedRole,
      search: valueRUT,
      surveys_responded: selectedSurveys
    });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    const newItemsPerPageNum = Number(newItemsPerPage);
    setItemsPerPage(newItemsPerPageNum);
    setCurrentPage(1);
    
    // Guardar en localStorage con los nuevos valores
    try {
      const filtersToSave = {
        filters: filters,
        itemsPerPage: newItemsPerPageNum,
        currentPage: 1,
        timestamp: Date.now()
      };
      localStorage.setItem('suscriptions-filters', JSON.stringify(filtersToSave));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
    
    debouncedFetchCustomers({
      page: 1,
      perPage: newItemsPerPageNum,
      suscription: selectedSuscription,
      group: selectedGroup || defaultGroup,
      role: selectedRole,
      search: valueRUT,
      surveys_responded: selectedSurveys
    });
  };

  const onSubmit = async (data) => {
    setLoadingNewCustomer(true);
    try {
      let registrationResult = { success: true }; // Default value
  
      if (moduleStatus[MODULES.SURVEYS]) {
        registrationResult = await signUpApoderado({
          username: data.username,
          email: data.email,
          password: data.password,
          first_name: data.first_name,
          last_name: data.last_name,
          role: data.role || 'customer'
        });
      }

      // Preparar datos del cliente incluyendo suscripción
      const customerData = {
        ...data,
        meta_data: [
          ...(data.group ? [{ key: 'group_name', value: data.group }] : []),
          ...(data.suscription ? [{ key: 'suscription_name', value: data.suscription }] : [])
        ]
      };
  
      const customerResult = await createCustomer(customerData);
  
      if (registrationResult.success && customerResult.success) {
        setFormMessage({ 
          type: "success", // Corregido a "success"
          message: "Usuario creado exitosamente" 
        });
        setTimeout(() => {
          setCurrentPage(1);
          fetchCustomers({ search: customerResult.data.username, page: 1 });
          reset();
          onClose();
        }, 2000);
      } else {
        const errorMessage = [
          registrationResult.message, 
          customerResult.message
        ].filter(Boolean).join('. ');
  
        setFormMessage({ 
          type: "error",
          message: errorMessage 
        });
      }
    } catch (error) {
      setFormMessage({ 
        type: "error", 
        message: "Error al crear usuario" 
      });
    } finally {
      setLoadingNewCustomer(false);
    }
  };

  const handleGroupSelection = (surveys) => {
    // Actualizar el filtro con las encuestas seleccionadas
    setFilters(prev => prev.map(f => {
      if (f.name === 'surveys_responded') {
        return {
          ...f,
          value: surveys
        };
      }
      return f;
    }));
    
    // Convertir array a string
    const surveysString = surveys.join(',');
    setSelectedSurveys(surveysString);
    setIncludeUserSurveys(!!surveysString);
    
    // Resetear a primera página
    setCurrentPage(1);
    
    // Usar la versión con debounce
    debouncedFetchCustomers({ 
      page: 1,
      surveys_responded: surveysString 
    });
  };

 // Load initial data with single effect
 useEffect(() => {
  const loadInitialData = async () => {
    try {
      const config = JSON.parse(getSiteConfig());
      const loadedGroups = config?.groups || [];
      setGroups(loadedGroups);

      // Cargar grupos normalizados desde la DB
      let groupOptions = [
        { value: '', label: 'TODOS LOS CURSOS' },
        { value: 'empty', label: 'SIN CURSO ASIGNADO' },
      ];
      try {
        const rawGroups = await getUserGroups();
        const normalized = buildNormalizedGroupOptions(rawGroups);
        groupOptions = [
          ...groupOptions,
          ...normalized.map(g => ({
            value: JSON.stringify(g.rawValues), // array como JSON
            label: g.label,
            rawValues: g.rawValues,
          }))
        ];
      } catch (e) {
        // Fallback a config.groups si el endpoint falla
        groupOptions = [
          ...groupOptions,
          ...loadedGroups.map(grupo => ({ value: grupo.name, label: grupo.name }))
        ];
      }

      // Update group filters
      setFilters(prev => prev.map(f => {
        if (f.name === 'group') {
          return {
            ...f,
            options: groupOptions,
          };
        }
        // Las opciones de 'role' las maneja solo el efecto de isAdmin (este efecto usa un isAdmin desactualizado)
        return f;
      }));

      // Load subscriptions if module active
      if (moduleStatus[MODULES.SUBSCRIPTIONS]) {
        const plans = await getProductsByCategories('planes');
        setSuscriptions(plans || []);
        
        // Load all products for user creation
        const allProducts = await getProducts();
        setProducts(allProducts || []);
        
        setFilters(prev => prev.map(f => {
          if (f.name === 'suscription') {
            return {
              ...f,
              options: [
                { value: '', label: 'TODAS LAS MENSUALIDADES' },
                ...(plans || []).map(plan => ({
                  value: plan.name,
                  label: plan.name
                }))
              ]
            };
          }
          return f;
        }));
      }

      // Cargar lista de encuestas si el módulo está activo
      if (moduleStatus[MODULES.SURVEYS]) {
        // Opciones fijas que siempre deben aparecer aunque no estén en la API
        const FIXED_SURVEY_OPTIONS = [
          { value: 'becas-2026', label: 'Becas 2026' },
          { value: 'becas-2026-archivos', label: 'Becas 2026 Archivos' },
          { value: 'becas-2027-socioeconomica', label: 'Becas 2027 Socioeconómica' },
          { value: 'becas-2027-funcionario', label: 'Becas 2027 Hijo/a de funcionario' },
          { value: 'becas-2027-cambio-economico', label: 'Becas 2027 Cambio económico' }
        ];

        try {
          const surveysResponse = await getSurveys();
          console.log('Encuestas:', surveysResponse);
          if (Array.isArray(surveysResponse)) {
            setSurveysList(surveysResponse);

            // Mezclar opciones de la API con las fijas, evitando duplicados por slug
            const apiOptions = surveysResponse.map(survey => ({
              value: survey.slug,
              label: survey.name || `Encuesta #${survey.id}`
            }));
            const apiSlugs = new Set(apiOptions.map(o => o.value));
            const mergedOptions = [
              ...apiOptions,
              ...FIXED_SURVEY_OPTIONS.filter(o => !apiSlugs.has(o.value))
            ];

            setFilters(prev => prev.map(f => {
              if (f.name === 'surveys_responded') {
                return { ...f, options: mergedOptions };
              }
              return f;
            }));
          }
        } catch (surveyError) {
          console.error('Error cargando encuestas:', surveyError);
          // Si la API falla, al menos mostrar las opciones fijas
          setFilters(prev => prev.map(f => {
            if (f.name === 'surveys_responded') {
              return { ...f, options: FIXED_SURVEY_OPTIONS };
            }
            return f;
          }));
        }
      }

      // Fetch customers with default group solo después de aplicar estados iniciales
      // Esta será la única carga inicial
      const initialStates = getInitialStates();
      setTimeout(() => {
        fetchCustomers({ 
          groupName: initialStates.selectedGroup || defaultGroup,
          search: initialStates.valueRUT,
          role: initialStates.selectedRole,
          suscriptionName: initialStates.selectedSuscription,
          surveys_responded: initialStates.selectedSurveys,
          page: initialStates.currentPage
        });
      }, 100); // Pequeño delay para asegurar que los estados estén aplicados
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };
  
  loadInitialData();
}, [moduleStatus, fetchCustomers, defaultGroup]);

  
// group_name puede ser JSON de array de rawValues (dropdown normalizado de cursos)
const resolveGroupName = (groupName) => {
  try {
    const parsed = JSON.parse(groupName);
    if (Array.isArray(parsed)) return parsed;
  } catch (_) {}
  return groupName;
};

// Trae TODOS los customers con los filtros activos (todas las páginas)
const fetchAllCustomers = useCallback(async () => {
  const PER_PAGE = 100;
  const totalPages = Math.ceil(totalCustomers / PER_PAGE) || 1;
  const all = [];
  for (let page = 1; page <= totalPages; page++) {
    const response = await getCustomers({
      suscription_name: selectedSuscription,
      group_name: resolveGroupName(selectedGroup || defaultGroup),
      search: valueRUT,
      role: selectedRole,
      surveys_responded: selectedSurveys,
      include_surveys: selectedSurveys ? 'false' : undefined,
      page,
      per_page: PER_PAGE,
    });
    all.push(...(response?.data || []));
  }
  return all;
}, [totalCustomers, selectedSuscription, selectedGroup, defaultGroup, valueRUT, selectedRole, selectedSurveys]);

const handleExcelExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const PER_PAGE = 100;
      const totalPagesToFetch = Math.ceil(totalCustomers / PER_PAGE);
      const allCustomers = [];

      for (let page = 1; page <= totalPagesToFetch; page++) {
        const response = await getCustomers({
          suscription_name: selectedSuscription,
          group_name: resolveGroupName(selectedGroup || defaultGroup),
          search: valueRUT,
          role: selectedRole,
          surveys_responded: selectedSurveys,
          include_surveys: selectedSurveys ? 'false' : undefined,
          page,
          per_page: PER_PAGE
        });
        const pageData = response?.data || [];
        allCustomers.push(...pageData);
      }

      const toStatus = (c) => {
        if (Array.isArray(c.roles)) {
          if (c.roles.includes('customer')) return 'Activo';
          if (c.roles.includes('becado')) return 'Becado';
          if (c.roles.includes('cobranza')) return 'Cobranza';
          if (c.roles.includes('subscriber')) return 'Archivado';
        }
        if (c.role === 'customer') return 'Activo';
        if (c.role === 'becado') return 'Becado';
        if (c.role === 'cobranza') return 'Cobranza';
        if (c.role === 'subscriber') return 'Archivado';
        return '';
      };

      const rows = allCustomers.map(c => ({
        RUT: c.username || '',
        Nombres: c.first_name || '',
        Apellidos: c.last_name || '',
        'Nombre completo': `${c.first_name || ''} ${c.last_name || ''}`.trim(),
        Email: c.email || '',
        Grupo: c.meta_data?.find(m => m.key === 'group_name')?.value || '',
        Mensualidad: c.meta_data?.find(m => m.key === 'suscription_name')?.value || '',
        Estado: toStatus(c)
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');

      const colWidths = Object.keys(rows[0] || {}).map(key => ({
        wch: Math.max(key.length, ...rows.map(r => String(r[key] || '').length))
      }));
      worksheet['!cols'] = colWidths;

      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `usuarios_${timestamp}.xlsx`);
    } catch (error) {
      console.error('Error exportando Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

return (
  <Flex direction='column' maxW="100%" fontFamily="jakarta">
    <Grid templateColumns={{ sm: "1fr", md: "1fr", lg: "1fr" }} gap='24px'>
      <Box>

        {moduleStatus[MODULES.SURVEYS] && (
          <SurveyGroupSelector onSelectGroup={handleGroupSelection} />
        )}

        {/* Stats strip */}
        {!loading && customers.length > 0 && (() => {
          const now = new Date();
          const newThisMonth = customers.filter(c => {
            if (!c.date_created) return false;
            const d = new Date(c.date_created);
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
          }).length;
          const withoutGroup = customers.filter(c => !c.meta_data?.find(m => m.key === 'group_name')?.value).length;
          const uniqueGroups = new Set(customers.map(c => c.meta_data?.find(m => m.key === 'group_name')?.value).filter(Boolean)).size;
          const statCards = [
            { icon: FiUsers,       value: totalCustomers, label: 'Total alumnos',    iconBg: 'coral.soft', iconColor: 'coral.700' },
            { icon: FiUserPlus,    value: newThisMonth,   label: 'Nuevos este mes',  iconBg: 'mint.bg',    iconColor: 'mint.ink'  },
            { icon: FiAlertCircle, value: withoutGroup,   label: 'Sin curso',        iconBg: 'butter.bg',  iconColor: 'butter.ink'},
            { icon: FiBookOpen,    value: uniqueGroups,   label: 'Cursos (pág.)',    iconBg: 'lav.bg',     iconColor: 'lav.ink'   },
          ];
          return (
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing="12px" mb="16px">
              {statCards.map((s, i) => (
                <Flex key={i} bg="white" border="1px solid" borderColor="cream.line" borderRadius="13px" p="13px 18px" align="center">
                  <Flex w="38px" h="38px" borderRadius="11px" bg={s.iconBg} align="center" justify="center" flexShrink={0}>
                    <Icon as={s.icon} boxSize="19px" color={s.iconColor} />
                  </Flex>
                  <Box ml="14px">
                    <Text fontSize="22px" fontWeight="800" letterSpacing="-0.02em" lineHeight="1" fontFamily="jakarta">{s.value}</Text>
                    <Text fontSize="12px" color="ink.400" fontWeight="600" mt="2px" fontFamily="jakarta">{s.label}</Text>
                  </Box>
                </Flex>
              ))}
            </SimpleGrid>
          );
        })()}

        <DynamicPaginator
          filters={filters}
          onFilterChange={handleFilterChange}
          totalItems={totalCustomers}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          isLoading={loading}
          onExport={customers.length > 0 ? handleExcelExport : undefined}
          isExporting={isExporting}
        >
          {loading ? (
            <Fade in={loading}>
              <VStack spacing={4} align="stretch" p={4}>
                <HStack justify="space-between" align="center" mb={4}>
                  <Skeleton height="20px" width="150px" />
                  <Skeleton height="30px" width="100px" />
                </HStack>
                {Array.from({ length: 5 }).map((_, index) => (
                  <Box
                    key={index}
                    p={4}
                    bg={useColorModeValue('white', 'gray.700')}
                    borderRadius="lg"
                    border="1px"
                    borderColor={useColorModeValue('gray.200', 'gray.600')}
                  >
                    <HStack spacing={4}>
                      <Skeleton height="40px" width="40px" borderRadius="full" />
                      <VStack align="stretch" spacing={2} flex={1}>
                        <Skeleton height="16px" width="200px" />
                        <Skeleton height="14px" width="150px" />
                      </VStack>
                      <HStack spacing={2}>
                        <Skeleton height="24px" width="60px" />
                        <Skeleton height="30px" width="30px" />
                      </HStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Fade>
          ) : customers.length > 0 ? (
            <Fade in={true}>
              <Customers
                title={customersConfig.title}
                customers={customers}
                showActionsMenu={customersConfig.showActionsMenu}
                defaultAction={customersConfig.defaultAction}
                onOpen={onOpen}
                onRefresh={() => fetchCustomers({ groupName: selectedGroup || defaultGroup })}
                totalCustomers={totalCustomers}
                onFetchAll={fetchAllCustomers}
              />
            </Fade>
          ) : (
            <Fade in={true}>
              <Center py={16}>
                <VStack spacing={6} textAlign="center" maxW="400px">
                  <Box
                    w="80px"
                    h="80px"
                    bg={useColorModeValue('gray.100', 'gray.700')}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="2xl">👥</Text>
                  </Box>
                  <VStack spacing={2}>
                    <Text fontSize="lg" fontWeight="semibold" color={useColorModeValue('gray.600', 'gray.300')}>
                      No se encontraron usuarios
                    </Text>
                    <Text fontSize="sm" color={useColorModeValue('gray.500', 'gray.400')} lineHeight="1.5">
                      {(valueRUT || selectedGroup || selectedRole || selectedSuscription || selectedSurveys) 
                        ? 'Intenta ajustar los filtros de búsqueda para obtener más resultados'
                        : 'Parece que aún no hay usuarios registrados en el sistema'
                      }
                    </Text>
                  </VStack>
                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={() => fetchCustomers({ groupName: selectedGroup || defaultGroup })}
                    leftIcon={<RepeatIcon />}
                  >
                    Refrescar
                  </Button>
                </VStack>
              </Center>
            </Fade>
          )}
        </DynamicPaginator>

        <Modal
            initialFocusRef={initialRef}
            finalFocusRef={finalRef}
            isOpen={isOpen}
            onClose={onClose}
          >
            <ModalOverlay />
            <ModalContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                <ModalHeader>Crear nueva cuenta (Pagos/Matriculas)</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                  <FormControl isInvalid={errors.username}>
                    <FormLabel htmlFor="username">RUT</FormLabel>
                    <Input
                      ref={initialRef}
                      type="text"
                      id="username"
                      {...register("username", { required: true })}
                      onChange={(e) => e.target.value = format(e.target.value, { dots: false })}
                    />
                  </FormControl>

                  <FormControl isInvalid={errors.email} mt={4}>
                    <FormLabel htmlFor="email">Email</FormLabel>
                    <Input
                      type="email"
                      id="email"
                      {...register("email", { required: true, pattern: /^\S+@\S+$/i })}
                    />
                  </FormControl>

                  <FormControl isInvalid={errors.first_name} mt={4}>
                    <FormLabel htmlFor="first_name">Nombres</FormLabel>
                    <Input 
                      type="text" 
                      id="first_name" 
                      {...register("first_name", { required: true })} 
                    />
                  </FormControl>

                  <FormControl isInvalid={errors.last_name} mt={4}>
                    <FormLabel htmlFor="last_name">Apellidos</FormLabel>
                    <Input 
                      type="text" 
                      id="last_name" 
                      {...register("last_name", { required: true })} 
                    />
                  </FormControl>

                  <FormControl isInvalid={errors.password} mt={4}>
                    <FormLabel htmlFor="password">Contraseña</FormLabel>
                    <Input 
                      type="text" 
                      id="password" 
                      {...register("password", { required: true })} 
                    />
                  </FormControl>

                  <FormControl isInvalid={errors.group} mt={4}>
                  <FormLabel htmlFor="group">Grupo</FormLabel>
                  <Select
                    id="group"
                    placeholder="Seleccione un grupo"
                    {...register("group")}
                  >
                    <option value="">Sin grupo</option>
                    {groups.map((grupo) => (
                      <option key={grupo.name} value={grupo.name}>
                        {grupo.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {errors.group && errors.group.message}
                  </FormErrorMessage>
                </FormControl>

                {moduleStatus[MODULES.SUBSCRIPTIONS] && (
                  <FormControl isInvalid={errors.suscription} mt={4}>
                    <FormLabel htmlFor="suscription">Suscripción</FormLabel>
                    <Select
                      id="suscription"
                      placeholder="Seleccione una suscripción"
                      {...register("suscription")}
                    >
                      <option value="">Sin suscripción</option>
                      {products.map((product) => (
                        <option key={product.id} value={product.name}>
                          {product.name} - ${product.price}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.suscription && errors.suscription.message}
                    </FormErrorMessage>
                  </FormControl>
                )}

                <FormMessage formMessage={formMessage} />
                </ModalBody>

                <ModalFooter>
                  <Button 
                    mr={4} 
                    colorScheme="teal" 
                    type="submit" 
                    isLoading={loadingNewCustomer}
                  >
                    Guardar
                  </Button>
                  <Button onClick={cancelNewCustomer}>Cancelar</Button>
                </ModalFooter>
              </form>
            </ModalContent>
          </Modal>
      </Box>
    </Grid>
  </Flex>
);
}

const FormMessage = ({ formMessage }) => {
  if (!formMessage) return null;

  return (
    <Alert mt={'6'} status={formMessage.type}>
      <AlertIcon />
      <Box flex="1">
        <Text>{formMessage.message}</Text>
        {formMessage.errorMessages && formMessage.errorMessages.length > 0 && (
          <UnorderedList>
            {formMessage.errorMessages.map((error, index) => (
              <ListItem key={index}>{error}</ListItem>
            ))}
          </UnorderedList>
        )}
      </Box>
    </Alert>
  );
};

export default Suscriptions;